import type { ReactNode } from "react";
import { Button, Space } from "antd";
import {
    BaseItem as ZhBaseItem,
    Grid as ZhGrid,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

export type ProductDetailTabRecord = Record<string, unknown> & {
    id?: string | number;
    productConsignment?: Record<string, any>;
};

export interface ProductDetailTabProps {
    data?: ProductDetailTabRecord;
    items?: ProductDetailTabRecord[];
    children?: ReactNode;
}

/** 详情页子 Tab 沿用 Vue 的 section-card 容器，避免迁移占位面板改变页签的层级和间距。 */
export function ProductDetailTabSection({ title, children, className = "" }: { title: string; children: ReactNode; className?: string }) {
    return (
        <section aria-label={title} className={`section-card product-detail-tab-section ${className}`.trim()}>
            <div className="section-title">{title}</div>
            {children}
        </section>
    );
}

/** 旧 Vue zh-table 的空态仍保留表头和最小高度，避免显示通用迁移字段或备注输入框。 */
export function ProductDetailTabTable({
    columns,
    data = [],
    ariaLabel
}: {
    columns: TableColumnProps<ProductDetailTabRecord>[];
    data?: ProductDetailTabRecord[];
    ariaLabel: string;
}) {
    return (
        <div aria-label={ariaLabel} className="product-detail-tab-table">
            <ZhTable<ProductDetailTabRecord>
                columns={columns}
                data={data}
                pagination={false}
                rowKey={row => String(row.id ?? "row")}
                isShowResetBtn={false}
                isShowSearchBtn={false}
            />
        </div>
    );
}

/** 详情页的统计卡片保持旧 Vue 的横向四列网格，不再输出迁移组件的英文字段名。 */
export function ProductDetailTabMetrics({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
    return (
        <ZhGrid cols={4} gap={[16, 16]}>
            {items.map(item => (
                <ZhBaseItem key={item.label} colon={false} label={item.label} value={item.value} />
            ))}
        </ZhGrid>
    );
}

/** 费用页签使用旧 Vue 的收款/退款内嵌页签，操作按钮只在真实业务有回调时出现。 */
export function ProductDetailSubTabs({ active, onChange }: { active: string; onChange: (value: string) => void }) {
    return (
        <div className="admin-detail-tabs product-detail-sub-tabs" role="tablist" aria-label="费用明细子页签">
            {["收款", "退款"].map(label => (
                <Button
                    key={label}
                    aria-selected={active === label}
                    className={active === label ? "is-active" : ""}
                    role="tab"
                    type="text"
                    onClick={() => onChange(label)}
                >
                    {label}
                </Button>
            ))}
        </div>
    );
}

export const emptyProductDetailColumns = (labels: string[]): TableColumnProps<ProductDetailTabRecord>[] =>
    labels.map(label => ({
        key: label,
        label,
        prop: label,
        render: ({ row }) => String(row[label] ?? "--")
    }));

export function ProductDetailTabActions({ children }: { children?: ReactNode }) {
    return children ? <Space className="product-detail-tab-actions">{children}</Space> : null;
}
